// ============================================================
// Décodage du format RWG1 (grille de chunks, little-endian, gzip)
// ============================================================
//  0  4o   magic « RWG1 »            8  i64 version     24 u32 blockCount
//  4  u8   format = 1               16  i64 since       28 u8  levelLen, puis le level en UTF-8
//  5  u8   blockShift = 5
//  6  u16  flags (bit0 FULL : remplace toute la grille locale ; sinon delta)
// Puis, par bloc (tri bx puis bz) : i32 bx, i32 bz, i64 rev, u8 kind (0 = vide, 1 = cellules),
// et si kind = 1 : 1024 x u16, cellule ((cz&31)<<5)|(cx&31), valeur = idx du pays (0 = aucun).
// ============================================================

export const RWG1_FLAG_FULL = 1;

export interface Rwg1Block {
  bx: number;
  bz: number;
  rev: number;
  /** null = bloc vide (kind 0) : en delta, c'est un effacement complet du bloc. */
  cells: Uint16Array | null;
}

export interface Rwg1 {
  full: boolean;
  version: number;
  since: number;
  level: string;
  blocks: Rwg1Block[];
}

const CELLS = 1024;

/** Décompresse si les octets commencent par l'en-tête gzip (1f 8b), sinon les prend tels quels. */
async function gunzip(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const head = new Uint8Array(buf, 0, Math.min(2, buf.byteLength));
  if (head.length < 2 || head[0] !== 0x1f || head[1] !== 0x8b) return buf;
  const stream = new Response(buf).body!.pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

function i64(view: DataView, off: number): number {
  return Number(view.getBigInt64(off, true));
}

export async function decodeRwg1(raw: ArrayBuffer): Promise<Rwg1> {
  const buf = await gunzip(raw);
  const view = new DataView(buf);
  if (buf.byteLength < 29) throw new Error('Grille RWG1 tronquée');
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== 'RWG1') throw new Error('Grille illisible (en-tête RWG1 absent)');
  const format = view.getUint8(4);
  const shift = view.getUint8(5);
  if (format !== 1 || shift !== 5) throw new Error(`Format de grille non géré (format ${format}, blockShift ${shift})`);
  const flags = view.getUint16(6, true);
  const version = i64(view, 8);
  const since = i64(view, 16);
  const blockCount = view.getUint32(24, true);
  const levelLen = view.getUint8(28);
  const level = new TextDecoder().decode(new Uint8Array(buf, 29, levelLen));
  let off = 29 + levelLen;

  const blocks: Rwg1Block[] = new Array(blockCount);
  for (let b = 0; b < blockCount; b++) {
    if (off + 17 > buf.byteLength) throw new Error('Grille RWG1 tronquée');
    const bx = view.getInt32(off, true);
    const bz = view.getInt32(off + 4, true);
    const rev = i64(view, off + 8);
    const kind = view.getUint8(off + 16);
    off += 17;
    let cells: Uint16Array | null = null;
    if (kind === 1) {
      if (off + CELLS * 2 > buf.byteLength) throw new Error('Grille RWG1 tronquée');
      // Lecture à la DataView : l'offset n'est pas forcément pair, une vue Uint16Array directe
      // serait refusée (et dépendrait de l'endianness de la machine).
      cells = new Uint16Array(CELLS);
      for (let i = 0; i < CELLS; i++) cells[i] = view.getUint16(off + i * 2, true);
      off += CELLS * 2;
    } else if (kind !== 0) {
      throw new Error(`Bloc de grille inconnu (kind ${kind})`);
    }
    blocks[b] = { bx, bz, rev, cells };
  }
  return { full: (flags & RWG1_FLAG_FULL) !== 0, version, since, level, blocks };
}
